import React, { useState, useEffect } from 'react';
import API from '../../api';
import { toast } from 'react-toastify';

export default function AdminPayments() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [plans, setPlans] = useState([]);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);

  const [filter, setFilter] = useState({
    status: '',
    method: '',
    startDate: '',
    endDate: ''
  });

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  const [formData, setFormData] = useState({
    userId: '',
    planId: '',
    amount: '',
    method: 'manual',
    status: 'completed',
    transactionId: ''
  });

  useEffect(() => {
    fetchPayments();
  }, [filter, pagination.page]);

  useEffect(() => {
    if (showCreateModal) {
      fetchUsersAndPlans();
    }
  }, [showCreateModal]);

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', pagination.page);
      params.append('limit', pagination.limit);
      if (filter.status) params.append('status', filter.status);
      if (filter.method) params.append('method', filter.method);
      if (filter.startDate) params.append('startDate', filter.startDate);
      if (filter.endDate) params.append('endDate', filter.endDate);

      const response = await API.get(`/payments/admin/all?${params.toString()}`);
      setPayments(response.data.payments || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination.total,
        pages: response.data.pagination.pages
      }));
    } catch (error) {
      console.error('Error fetching payments:', error);
      toast.error('Failed to load payments');
    }
    setLoading(false);
  };

  const fetchUsersAndPlans = async () => {
    try {
      const [usersRes, plansRes] = await Promise.all([
        API.get('/users/all'),
        API.get('/subscriptions/plans')
      ]);
      setUsers(usersRes.data);
      setPlans(plansRes.data.plans);
    } catch (error) {
      toast.error('Failed to load users or plans');
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await API.post('/payments/admin/create', formData);
      toast.success('Payment recorded successfully');
      setShowCreateModal(false);
      fetchPayments();
      setFormData({ userId: '', planId: '', amount: '', method: 'manual', status: 'completed', transactionId: '' });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create payment');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await API.put(`/payments/admin/${selectedPayment._id}`, formData);
      toast.success('Payment updated successfully');
      setShowEditModal(false);
      fetchPayments();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update payment');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure? This will remove the payment record permanently.')) return;
    try {
      await API.delete(`/payments/admin/${id}`);
      toast.success('Payment deleted');
      fetchPayments();
    } catch (error) {
      toast.error('Failed to delete payment');
    }
  };

  const openEditModal = (payment) => {
    setSelectedPayment(payment);
    setFormData({
      status: payment.status,
      method: payment.method,
      amount: payment.amountInRupees,
      transactionId: payment.esewaPayload?.transactionCode || ''
    });
    setShowEditModal(true);
  };

  const formatDate = (date) => new Date(date).toLocaleString();
  const formatPrice = (amount) => `Rs. ${amount?.toLocaleString() || 0}`;

  const getStatusConfig = (status) => {
    switch (status) {
      case 'completed': return { bg: 'linear-gradient(135deg, #d1fae5, #a7f3d0)', color: '#065f46', icon: '✓', label: 'Completed' };
      case 'pending': return { bg: 'linear-gradient(135deg, #fef3c7, #fde68a)', color: '#92400e', icon: '⏳', label: 'Pending' };
      case 'failed': return { bg: 'linear-gradient(135deg, #fee2e2, #fecaca)', color: '#991b1b', icon: '✕', label: 'Failed' };
      case 'refunded': return { bg: 'linear-gradient(135deg, #e0e7ff, #c7d2fe)', color: '#3730a3', icon: '↩', label: 'Refunded' };
      default: return { bg: 'linear-gradient(135deg, #f1f5f9, #e2e8f0)', color: '#475569', icon: '•', label: status };
    }
  };

  const getMethodIcon = (method) => {
    switch (method) {
      case 'esewa': return '📱';
      case 'manual': return '💵';
      case 'bank': return '🏦';
      default: return '💳';
    }
  };

  const completedCount = payments.filter(p => p.status === 'completed').length;
  const pendingCount = payments.filter(p => p.status === 'pending').length;
  const failedCount = payments.filter(p => p.status === 'failed').length;
  const totalRevenue = payments.filter(p => p.status === 'completed').reduce((sum, p) => sum + (p.amountInRupees || 0), 0);

  return (
    <div style={styles.pageWrapper}>
      <div style={styles.container}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <div style={styles.titleRow}>
              <div style={styles.titleIcon}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
                  <line x1="1" y1="10" x2="23" y2="10"/>
                </svg>
              </div>
              <div>
                <h1 style={styles.title}>Payment Management</h1>
                <p style={styles.subtitle}>Track and manage all subscription payments</p>
              </div>
            </div>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            style={styles.createBtn}
            onMouseEnter={e => { e.target.style.transform = 'translateY(-2px)'; e.target.style.boxShadow = '0 8px 25px rgba(99, 102, 241, 0.4)'; }}
            onMouseLeave={e => { e.target.style.transform = 'translateY(0)'; e.target.style.boxShadow = '0 4px 15px rgba(99, 102, 241, 0.3)'; }}
          >
            <span style={{ fontSize: '18px', marginRight: '8px' }}>+</span>
            Record Manual Payment
          </button>
        </div>

        {/* Stats Cards */}
        <div style={styles.statsRow}>
          <div style={{ ...styles.statCard, borderLeft: '4px solid #6366f1' }}>
            <div style={styles.statIconWrap}>
              <div style={{ ...styles.statIconCircle, background: 'linear-gradient(135deg, #eef2ff, #e0e7ff)' }}>
                <span style={{ fontSize: '20px' }}>📊</span>
              </div>
            </div>
            <div>
              <p style={styles.statLabel}>Total Payments</p>
              <h3 style={styles.statValue}>{pagination.total}</h3>
            </div>
          </div>
          <div style={{ ...styles.statCard, borderLeft: '4px solid #10b981' }}>
            <div style={styles.statIconWrap}>
              <div style={{ ...styles.statIconCircle, background: 'linear-gradient(135deg, #d1fae5, #a7f3d0)' }}>
                <span style={{ fontSize: '20px' }}>💰</span>
              </div>
            </div>
            <div>
              <p style={styles.statLabel}>Total Revenue</p>
              <h3 style={{ ...styles.statValue, color: '#059669' }}>{formatPrice(totalRevenue)}</h3>
            </div>
          </div>
          <div style={{ ...styles.statCard, borderLeft: '4px solid #f59e0b' }}>
            <div style={styles.statIconWrap}>
              <div style={{ ...styles.statIconCircle, background: 'linear-gradient(135deg, #fef3c7, #fde68a)' }}>
                <span style={{ fontSize: '20px' }}>⏳</span>
              </div>
            </div>
            <div>
              <p style={styles.statLabel}>Pending</p>
              <h3 style={{ ...styles.statValue, color: '#d97706' }}>{pendingCount}</h3>
            </div>
          </div>
          <div style={{ ...styles.statCard, borderLeft: '4px solid #ef4444' }}>
            <div style={styles.statIconWrap}>
              <div style={{ ...styles.statIconCircle, background: 'linear-gradient(135deg, #fee2e2, #fecaca)' }}>
                <span style={{ fontSize: '20px' }}>❌</span>
              </div>
            </div>
            <div>
              <p style={styles.statLabel}>Failed</p>
              <h3 style={{ ...styles.statValue, color: '#dc2626' }}>{failedCount}</h3>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div style={styles.filtersContainer}>
          <div style={styles.filtersInner}>
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Status</label>
              <select
                style={styles.filterSelect}
                value={filter.status}
                onChange={e => setFilter({ ...filter, status: e.target.value })}
              >
                <option value="">All Statuses</option>
                <option value="completed">Completed</option>
                <option value="pending">Pending</option>
                <option value="failed">Failed</option>
                <option value="refunded">Refunded</option>
              </select>
            </div>
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Method</label>
              <select
                style={styles.filterSelect}
                value={filter.method}
                onChange={e => setFilter({ ...filter, method: e.target.value })}
              >
                <option value="">All Methods</option>
                <option value="esewa">eSewa</option>
                <option value="manual">Manual / Cash</option>
                <option value="bank">Bank Transfer</option>
              </select>
            </div>
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Start Date</label>
              <input
                type="date"
                style={styles.filterInput}
                value={filter.startDate}
                onChange={e => setFilter({ ...filter, startDate: e.target.value })}
              />
            </div>
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>End Date</label>
              <input
                type="date"
                style={styles.filterInput}
                value={filter.endDate}
                onChange={e => setFilter({ ...filter, endDate: e.target.value })}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
              <button
                onClick={() => setFilter({ status: '', method: '', startDate: '', endDate: '' })}
                style={styles.clearFilterBtn}
              >
                Clear
              </button>
              <button onClick={fetchPayments} style={styles.refreshBtn}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '6px' }}>
                  <polyline points="23 4 23 10 17 10"/>
                  <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
                </svg>
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Table */}
        <div style={styles.tableContainer}>
          {loading ? (
            <div style={styles.loadingContainer}>
              <div style={styles.spinner}></div>
              <p style={styles.loadingText}>Loading payments...</p>
            </div>
          ) : payments.length === 0 ? (
            <div style={styles.emptyState}>
              <div style={styles.emptyIcon}>💳</div>
              <h3 style={styles.emptyTitle}>No payments found</h3>
              <p style={styles.emptyText}>Try adjusting your filters or record a new payment.</p>
            </div>
          ) : (
            <>
              <div style={styles.tableHeader}>
                <p style={styles.tableCount}>
                  Showing <strong>{payments.length}</strong> of <strong>{pagination.total}</strong> payments
                </p>
              </div>
              <div style={styles.tableScroll}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Date</th>
                      <th style={styles.th}>User</th>
                      <th style={styles.th}>Plan</th>
                      <th style={styles.th}>Amount</th>
                      <th style={styles.th}>Method</th>
                      <th style={styles.th}>Status</th>
                      <th style={{ ...styles.th, textAlign: 'center' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((payment, index) => {
                      const statusConfig = getStatusConfig(payment.status);
                      return (
                        <tr
                          key={payment._id}
                          style={{
                            ...styles.tr,
                            background: index % 2 === 0 ? '#ffffff' : '#fafbfc',
                          }}
                          onMouseEnter={e => { e.currentTarget.style.background = '#f0f4ff'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = index % 2 === 0 ? '#ffffff' : '#fafbfc'; }}
                        >
                          <td style={styles.td}>
                            <div style={styles.dateCell}>
                              <span style={styles.dateMain}>
                                {new Date(payment.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                              </span>
                              <span style={styles.dateTime}>
                                {new Date(payment.createdAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                          </td>
                          <td style={styles.td}>
                            <div style={styles.userCell}>
                              <div style={styles.userAvatar}>
                                {(payment.user?.name || 'U').charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <div style={styles.userName}>{payment.user?.name || 'Unknown'}</div>
                                <div style={styles.userEmail}>{payment.user?.email || '—'}</div>
                              </div>
                            </div>
                          </td>
                          <td style={styles.td}>
                            <span style={styles.planBadge}>{payment.plan?.name || 'N/A'}</span>
                          </td>
                          <td style={styles.td}>
                            <span style={styles.amountText}>{formatPrice(payment.amountInRupees)}</span>
                          </td>
                          <td style={styles.td}>
                            <div style={styles.methodCell}>
                              <span style={{ fontSize: '16px' }}>{getMethodIcon(payment.method)}</span>
                              <span style={styles.methodText}>{payment.method}</span>
                            </div>
                          </td>
                          <td style={styles.td}>
                            <span style={{
                              ...styles.statusBadge,
                              background: statusConfig.bg,
                              color: statusConfig.color
                            }}>
                              <span style={{ marginRight: '4px', fontSize: '10px' }}>{statusConfig.icon}</span>
                              {statusConfig.label}
                            </span>
                          </td>
                          <td style={{ ...styles.td, textAlign: 'center' }}>
                            <div style={styles.actionBtns}>
                              <button
                                onClick={() => openEditModal(payment)}
                                style={styles.editBtn}
                                title="Edit Payment"
                                onMouseEnter={e => { e.target.style.background = '#eef2ff'; e.target.style.borderColor = '#818cf8'; }}
                                onMouseLeave={e => { e.target.style.background = '#f8fafc'; e.target.style.borderColor = '#e2e8f0'; }}
                              >
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                                </svg>
                              </button>
                              <button
                                onClick={() => handleDelete(payment._id)}
                                style={styles.deleteBtn}
                                title="Delete Payment"
                                onMouseEnter={e => { e.target.style.background = '#fef2f2'; e.target.style.borderColor = '#f87171'; }}
                                onMouseLeave={e => { e.target.style.background = '#f8fafc'; e.target.style.borderColor = '#e2e8f0'; }}
                              >
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="3 6 5 6 21 6"/>
                                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                                  <line x1="10" y1="11" x2="10" y2="17"/>
                                  <line x1="14" y1="11" x2="14" y2="17"/>
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.pages > 1 && (
                <div style={styles.paginationContainer}>
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                    disabled={pagination.page === 1}
                    style={{
                      ...styles.pageBtn,
                      opacity: pagination.page === 1 ? 0.4 : 1,
                      cursor: pagination.page === 1 ? 'not-allowed' : 'pointer'
                    }}
                  >
                    ← Previous
                  </button>
                  <div style={styles.pageInfo}>
                    Page <strong>{pagination.page}</strong> of <strong>{pagination.pages}</strong>
                  </div>
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    disabled={pagination.page === pagination.pages}
                    style={{
                      ...styles.pageBtn,
                      opacity: pagination.page === pagination.pages ? 0.4 : 1,
                      cursor: pagination.page === pagination.pages ? 'not-allowed' : 'pointer'
                    }}
                  >
                    Next →
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* CREATE MODAL */}
        {showCreateModal && (
          <div style={styles.modalOverlay} onClick={() => setShowCreateModal(false)}>
            <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
              <div style={styles.modalHeader}>
                <div style={styles.modalIconWrap}>
                  <span style={{ fontSize: '24px' }}>💵</span>
                </div>
                <div>
                  <h2 style={styles.modalTitle}>Record Manual Payment</h2>
                  <p style={styles.modalSubtitle}>Add a new payment entry to the system</p>
                </div>
                <button onClick={() => setShowCreateModal(false)} style={styles.modalCloseBtn}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>
              <div style={styles.modalDivider}></div>
              <form onSubmit={handleCreate}>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>
                    <span style={styles.labelIcon}>👤</span> User
                  </label>
                  <select
                    style={styles.formInput}
                    value={formData.userId}
                    onChange={(e) => setFormData({ ...formData, userId: e.target.value })}
                    required
                  >
                    <option value="">Select a user...</option>
                    {users.map(u => <option key={u._id} value={u._id}>{u.name} ({u.email})</option>)}
                  </select>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>
                    <span style={styles.labelIcon}>📋</span> Plan
                  </label>
                  <select
                    style={styles.formInput}
                    value={formData.planId}
                    onChange={(e) => setFormData({ ...formData, planId: e.target.value })}
                    required
                  >
                    <option value="">Select a plan...</option>
                    {plans.map(p => <option key={p._id} value={p._id}>{p.name} - Rs.{p.price / 100}</option>)}
                  </select>
                </div>
                <div style={styles.formRow}>
                  <div style={{ ...styles.formGroup, flex: 1 }}>
                    <label style={styles.formLabel}>
                      <span style={styles.labelIcon}>💰</span> Amount (Rs)
                    </label>
                    <input
                      type="number"
                      style={styles.formInput}
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      placeholder="0.00"
                      required
                    />
                  </div>
                  <div style={{ ...styles.formGroup, flex: 1 }}>
                    <label style={styles.formLabel}>
                      <span style={styles.labelIcon}>🏦</span> Method
                    </label>
                    <select
                      style={styles.formInput}
                      value={formData.method}
                      onChange={(e) => setFormData({ ...formData, method: e.target.value })}
                    >
                      <option value="manual">Cash / Manual</option>
                      <option value="bank">Bank Transfer</option>
                    </select>
                  </div>
                </div>
                <div style={styles.modalDivider}></div>
                <div style={styles.modalActions}>
                  <button type="button" onClick={() => setShowCreateModal(false)} style={styles.cancelBtn}>
                    Cancel
                  </button>
                  <button type="submit" style={styles.saveBtn}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px' }}>
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    Create Record
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* EDIT MODAL */}
        {showEditModal && (
          <div style={styles.modalOverlay} onClick={() => setShowEditModal(false)}>
            <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
              <div style={styles.modalHeader}>
                <div style={{ ...styles.modalIconWrap, background: 'linear-gradient(135deg, #eef2ff, #e0e7ff)' }}>
                  <span style={{ fontSize: '24px' }}>✏️</span>
                </div>
                <div>
                  <h2 style={styles.modalTitle}>Edit Payment</h2>
                  <p style={styles.modalSubtitle}>Update payment status and details</p>
                </div>
                <button onClick={() => setShowEditModal(false)} style={styles.modalCloseBtn}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>
              <div style={styles.modalDivider}></div>
              <form onSubmit={handleUpdate}>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>
                    <span style={styles.labelIcon}>📌</span> Status
                  </label>
                  <select
                    style={styles.formInput}
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    <option value="pending">Pending</option>
                    <option value="completed">Completed</option>
                    <option value="failed">Failed</option>
                    <option value="refunded">Refunded</option>
                  </select>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>
                    <span style={styles.labelIcon}>🔗</span> Transaction ID
                    <span style={styles.optionalTag}>Optional</span>
                  </label>
                  <input
                    type="text"
                    style={styles.formInput}
                    value={formData.transactionId}
                    onChange={(e) => setFormData({ ...formData, transactionId: e.target.value })}
                    placeholder="Enter transaction reference..."
                  />
                </div>
                <div style={styles.modalDivider}></div>
                <div style={styles.modalActions}>
                  <button type="button" onClick={() => setShowEditModal(false)} style={styles.cancelBtn}>
                    Cancel
                  </button>
                  <button type="submit" style={{ ...styles.saveBtn, background: 'linear-gradient(135deg, #6366f1, #4f46e5)' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px' }}>
                      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
                      <polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/>
                    </svg>
                    Save Changes
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideIn {
          from { opacity: 0; transform: scale(0.95) translateY(-10px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>
  );
}

const styles = {
  pageWrapper: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #f0f4ff 0%, #faf5ff 50%, #f0fdf4 100%)',
  },
  container: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '32px 40px',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '32px',
  },
  headerLeft: {},
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  titleIcon: {
    width: '52px',
    height: '52px',
    background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
    borderRadius: '14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 15px rgba(99, 102, 241, 0.3)',
  },
  title: {
    fontSize: '28px',
    fontWeight: '800',
    color: '#0f172a',
    margin: 0,
    letterSpacing: '-0.5px',
  },
  subtitle: {
    fontSize: '14px',
    color: '#64748b',
    margin: '4px 0 0 0',
    fontWeight: '400',
  },
  createBtn: {
    background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
    color: 'white',
    border: 'none',
    padding: '12px 24px',
    borderRadius: '12px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
    boxShadow: '0 4px 15px rgba(99, 102, 241, 0.3)',
    transition: 'all 0.2s ease',
    letterSpacing: '0.2px',
  },

  // Stats
  statsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '20px',
    marginBottom: '28px',
  },
  statCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '20px 24px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.03)',
    transition: 'all 0.2s ease',
  },
  statIconWrap: {},
  statIconCircle: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  statLabel: {
    fontSize: '12px',
    color: '#94a3b8',
    margin: '0 0 4px 0',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  statValue: {
    fontSize: '24px',
    fontWeight: '800',
    color: '#0f172a',
    margin: 0,
  },

  // Filters
  filtersContainer: {
    background: 'white',
    borderRadius: '16px',
    padding: '20px 24px',
    marginBottom: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.03)',
  },
  filtersInner: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '16px',
    flexWrap: 'wrap',
  },
  filterGroup: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: '160px',
  },
  filterLabel: {
    fontSize: '11px',
    fontWeight: '700',
    color: '#94a3b8',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '6px',
  },
  filterSelect: {
    padding: '9px 14px',
    border: '1.5px solid #e2e8f0',
    borderRadius: '10px',
    fontSize: '13px',
    color: '#334155',
    background: '#f8fafc',
    outline: 'none',
    cursor: 'pointer',
    transition: 'border-color 0.2s',
  },
  filterInput: {
    padding: '9px 14px',
    border: '1.5px solid #e2e8f0',
    borderRadius: '10px',
    fontSize: '13px',
    color: '#334155',
    background: '#f8fafc',
    outline: 'none',
    transition: 'border-color 0.2s',
  },
  clearFilterBtn: {
    padding: '9px 18px',
    background: '#f1f5f9',
    border: '1.5px solid #e2e8f0',
    borderRadius: '10px',
    fontSize: '13px',
    fontWeight: '600',
    color: '#64748b',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  refreshBtn: {
    padding: '9px 18px',
    background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
    border: 'none',
    borderRadius: '10px',
    fontSize: '13px',
    fontWeight: '600',
    color: 'white',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    transition: 'all 0.2s',
    boxShadow: '0 2px 8px rgba(99, 102, 241, 0.25)',
  },

  // Table
  tableContainer: {
    background: 'white',
    borderRadius: '20px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 8px 30px rgba(0,0,0,0.06)',
    overflow: 'hidden',
  },
  tableHeader: {
    padding: '16px 24px',
    borderBottom: '1px solid #f1f5f9',
  },
  tableCount: {
    fontSize: '13px',
    color: '#64748b',
    margin: 0,
  },
  tableScroll: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
    padding: '14px 20px',
    textAlign: 'left',
    fontSize: '11px',
    fontWeight: '700',
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.8px',
    borderBottom: '2px solid #e2e8f0',
    whiteSpace: 'nowrap',
  },
  tr: {
    transition: 'background 0.15s ease',
    cursor: 'default',
  },
  td: {
    padding: '16px 20px',
    borderBottom: '1px solid #f1f5f9',
    fontSize: '14px',
    color: '#334155',
    verticalAlign: 'middle',
  },
  dateCell: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  dateMain: {
    fontWeight: '600',
    color: '#1e293b',
    fontSize: '13px',
  },
  dateTime: {
    fontSize: '11px',
    color: '#94a3b8',
  },
  userCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  userAvatar: {
    width: '38px',
    height: '38px',
    borderRadius: '10px',
    background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: '700',
    fontSize: '14px',
    flexShrink: 0,
  },
  userName: {
    fontWeight: '600',
    color: '#1e293b',
    fontSize: '13px',
  },
  userEmail: {
    fontSize: '11px',
    color: '#94a3b8',
    marginTop: '1px',
  },
  planBadge: {
    background: 'linear-gradient(135deg, #f0f4ff, #e8ecff)',
    color: '#4338ca',
    padding: '5px 14px',
    borderRadius: '8px',
    fontSize: '12px',
    fontWeight: '600',
    border: '1px solid #e0e7ff',
  },
  amountText: {
    fontWeight: '700',
    color: '#0f172a',
    fontSize: '14px',
    fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
  },
  methodCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  methodText: {
    fontSize: '13px',
    fontWeight: '500',
    color: '#475569',
    textTransform: 'capitalize',
  },
  statusBadge: {
    padding: '6px 14px',
    borderRadius: '20px',
    fontSize: '11px',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    display: 'inline-flex',
    alignItems: 'center',
  },
  actionBtns: {
    display: 'flex',
    gap: '8px',
    justifyContent: 'center',
  },
  editBtn: {
    width: '36px',
    height: '36px',
    borderRadius: '10px',
    border: '1.5px solid #e2e8f0',
    background: '#f8fafc',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
    padding: 0,
  },
  deleteBtn: {
    width: '36px',
    height: '36px',
    borderRadius: '10px',
    border: '1.5px solid #e2e8f0',
    background: '#f8fafc',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
    padding: 0,
  },

  // Pagination
  paginationContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '16px',
    padding: '20px 24px',
    borderTop: '1px solid #f1f5f9',
  },
  pageBtn: {
    padding: '8px 20px',
    background: 'white',
    border: '1.5px solid #e2e8f0',
    borderRadius: '10px',
    fontSize: '13px',
    fontWeight: '600',
    color: '#475569',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  pageInfo: {
    fontSize: '13px',
    color: '#64748b',
  },

  // Loading & Empty
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '80px 20px',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '3px solid #e2e8f0',
    borderTopColor: '#6366f1',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  loadingText: {
    marginTop: '16px',
    color: '#94a3b8',
    fontSize: '14px',
    fontWeight: '500',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '80px 20px',
  },
  emptyIcon: {
    fontSize: '48px',
    marginBottom: '16px',
    opacity: 0.6,
  },
  emptyTitle: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#334155',
    margin: '0 0 8px 0',
  },
  emptyText: {
    fontSize: '14px',
    color: '#94a3b8',
    margin: 0,
  },

  // Modal
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(15, 23, 42, 0.6)',
    backdropFilter: 'blur(8px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    animation: 'fadeIn 0.2s ease',
  },
  modalContent: {
    background: 'white',
    padding: '0',
    borderRadius: '20px',
    width: '480px',
    maxWidth: '90vw',
    boxShadow: '0 25px 60px rgba(0,0,0,0.25)',
    animation: 'slideIn 0.3s ease',
    overflow: 'hidden',
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '24px 28px 0 28px',
    position: 'relative',
  },
  modalIconWrap: {
    width: '48px',
    height: '48px',
    borderRadius: '14px',
    background: 'linear-gradient(135deg, #d1fae5, #a7f3d0)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  modalTitle: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#0f172a',
    margin: 0,
  },
  modalSubtitle: {
    fontSize: '13px',
    color: '#94a3b8',
    margin: '2px 0 0 0',
  },
  modalCloseBtn: {
    position: 'absolute',
    top: '20px',
    right: '20px',
    width: '36px',
    height: '36px',
    borderRadius: '10px',
    border: 'none',
    background: '#f1f5f9',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#64748b',
    transition: 'all 0.2s',
  },
  modalDivider: {
    height: '1px',
    background: '#f1f5f9',
    margin: '20px 28px',
  },
  formGroup: {
    marginBottom: '18px',
    padding: '0 28px',
  },
  formRow: {
    display: 'flex',
    gap: '16px',
  },
  formLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '8px',
  },
  labelIcon: {
    fontSize: '14px',
  },
  optionalTag: {
    fontSize: '10px',
    color: '#94a3b8',
    fontWeight: '500',
    marginLeft: 'auto',
    background: '#f1f5f9',
    padding: '2px 8px',
    borderRadius: '4px',
  },
  formInput: {
    width: '100%',
    padding: '11px 14px',
    border: '1.5px solid #e2e8f0',
    borderRadius: '10px',
    fontSize: '14px',
    color: '#1e293b',
    background: '#f8fafc',
    outline: 'none',
    transition: 'all 0.2s',
    boxSizing: 'border-box',
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    padding: '0 28px 24px 28px',
  },
  cancelBtn: {
    background: '#f1f5f9',
    border: '1.5px solid #e2e8f0',
    padding: '10px 22px',
    borderRadius: '10px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    color: '#64748b',
    transition: 'all 0.2s',
  },
  saveBtn: {
    background: 'linear-gradient(135deg, #10b981, #059669)',
    color: 'white',
    border: 'none',
    padding: '10px 24px',
    borderRadius: '10px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
    transition: 'all 0.2s',
  },
};